import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, Filter, SlidersHorizontal } from 'lucide-react';

import { SearchInput } from '../forms/SearchInput';
import { cn } from '@/shared/utils/cn';

import { FilterBar } from './FilterBar';

export interface ListFilterOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

export interface ListSortOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

export interface ListFiltersBarProps<
  TFilterValue extends string = string,
  TSortValue extends string = string,
> {
  ariaLabel: string;
  searchId: string;
  searchLabel: string;
  searchPlaceholder: string;
  query: string;
  onQueryChange: (next: string) => void;
  filterLabel: string;
  filterSectionLabel: string;
  filterValue: TFilterValue;
  filterDefaultValue: TFilterValue;
  filterOptions: Array<ListFilterOption<TFilterValue>>;
  onFilterChange: (next: TFilterValue) => void;
  sortLabel: string;
  sortValue: TSortValue;
  sortOptions: Array<ListSortOption<TSortValue>>;
  onSortChange: (next: TSortValue) => void;
}

export function ListFiltersBar<
  TFilterValue extends string = string,
  TSortValue extends string = string,
>({
  ariaLabel,
  searchId,
  searchLabel,
  searchPlaceholder,
  query,
  onQueryChange,
  filterLabel,
  filterSectionLabel,
  filterValue,
  filterDefaultValue,
  filterOptions,
  onFilterChange,
  sortLabel,
  sortValue,
  sortOptions,
  onSortChange,
}: ListFiltersBarProps<TFilterValue, TSortValue>) {
  const activeSortLabel = sortOptions.find((option) => option.value === sortValue)?.label ?? '';

  return (
    <FilterBar aria-label={ariaLabel}>
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full max-w-2xl flex-1 items-center gap-3">
          <SearchInput
            id={searchId}
            label={searchLabel}
            value={query}
            onValueChange={onQueryChange}
            placeholder={searchPlaceholder}
            className="flex-1"
          />

          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className={cn(
                  'flex h-9 shrink-0 items-center gap-2 rounded-sm border border-border bg-surface px-3 text-sm text-fg',
                  'hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                  filterValue !== filterDefaultValue &&
                    'border-primary/60 bg-primary-subtle text-primary',
                )}
              >
                <Filter aria-hidden="true" className="h-4 w-4 text-fg-muted" />
                {filterLabel}
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                sideOffset={6}
                align="start"
                className={cn(
                  'z-50 min-w-[180px] rounded-md border border-border bg-surface p-2 shadow-lg',
                  'data-[state=open]:animate-in data-[state=closed]:animate-out',
                  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                  'data-[side=bottom]:slide-in-from-top-1',
                )}
              >
                <div className="mb-1 px-2 py-1 text-xs font-semibold uppercase tracking-wider text-fg-muted">
                  {filterSectionLabel}
                </div>
                {filterOptions.map((option) => (
                  <DropdownMenu.Item
                    key={option.value}
                    onSelect={(event) => {
                      event.preventDefault();
                      onFilterChange(option.value);
                    }}
                    className={cn(
                      'flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-2 text-sm text-fg outline-none',
                      'data-[highlighted]:bg-surface-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        filterValue === option.value
                          ? 'border-primary bg-primary'
                          : 'border-border bg-surface',
                      )}
                    >
                      {filterValue === option.value ? (
                        <Check
                          className="h-3 w-3 text-fg-on-primary"
                          strokeWidth={3}
                          aria-hidden="true"
                        />
                      ) : null}
                    </span>
                    {option.label}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-sm px-2 text-sm text-fg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <SlidersHorizontal aria-hidden="true" className="h-4 w-4 text-fg-subtle" />
              <span className="text-fg-muted">{sortLabel}</span>
              <span className="font-semibold text-fg">{activeSortLabel}</span>
              <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 text-fg-subtle" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              sideOffset={6}
              align="end"
              className={cn(
                'z-50 min-w-[160px] rounded-md border border-border bg-surface p-1 shadow-lg',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                'data-[side=bottom]:slide-in-from-top-1',
              )}
            >
              {sortOptions.map((option) => (
                <DropdownMenu.Item
                  key={option.value}
                  onSelect={() => onSortChange(option.value)}
                  className={cn(
                    'flex cursor-pointer select-none items-center gap-2 rounded-sm px-3 py-2 text-sm text-fg outline-none',
                    'data-[highlighted]:bg-surface-muted',
                    sortValue === option.value && 'font-medium text-primary',
                  )}
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                    {sortValue === option.value ? (
                      <Check
                        className="h-3.5 w-3.5 text-primary"
                        strokeWidth={2.5}
                        aria-hidden="true"
                      />
                    ) : null}
                  </span>
                  {option.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </FilterBar>
  );
}
